// src/lib/trigger-builder.ts
// v72.19 — Doctrine v68 Phase-3g (seed-locked canonical + pack-count fix)
// - FIX: Canonical (seeded) path now **skips near-dup + anti-mash**; we only do exact (case-insensitive)
//        de-dup. This guarantees **10/10 seeds per canonical board** deterministically.
// - FIX: Removed cross-note "walk me through" global suppression so Starter Packs don’t lose counts.
// - KEEP: v72.18 leadership salvage (seed-safe), final anti-mash for non-canonical, CSM clean heads,
//         short-note clustering, deterministic; no matcher/audio changes.

import { CueNote } from "../types";

import generalPack from "../canonical-packs/general.json";
import aePack from "../canonical-packs/ae.json";
import csmPack from "../canonical-packs/csm.json";
import pmPack from "../canonical-packs/pm.json";
import swePack from "../canonical-packs/swe.json";
import mktPack from "../canonical-packs/marketing.json";

export type BuilderOptions = {
  synonyms?: Record<string, string[]>;
  fuzzyDistance?: number;
};

type LayerCaps = { total: number };
const DEFAULT_MAX: LayerCaps = { total: 10 };

// ---------------- Doctrine / safety ----------------

const RISKY_SINGLETON_BLOCKLIST = new Set([
  "background","role","plan","project","management"
]);

const GENERIC_CORE_NOUN_BLOCKLIST = new Set([
  "role","background","company","team","work","stuff","things","project","projects","plan","plans","management", "notes"
]);

const VETTED_TEMPLATES = {
  elevator: ["tell me about yourself","introduce yourself","about yourself","your resume","your cv","your background","take me through your resume"],
  role:     ["why this role","what attracted you","why this job","why this company","why us","why join us","this role"],
  strengths:["your strengths","biggest strengths","top strengths","key strength","strongest skill"],
  weaknesses:["your weaknesses","biggest weakness","areas to improve","improvement areas","areas for development"],
  roadblock:["major challenge","project roadblock","went wrong","big obstacle","time you failed"],
  leadership:["led a team","took ownership","managed people","influenced without authority","developed talent","lead by example"],
  career:  ["career goals","five years","long term goals","future plans","where you see yourself"],
  pm:      ["project management","manage projects","project plans","prioritize work","manage stakeholders"],
  metrics: ["key metrics","measure success","track kpis","performance metrics","success criteria"],
  salary:  ["salary expectations","expected salary","total compensation","salary range","desired pay"],
} as const;

const TWO_TOKEN_ALLOWLIST = new Set([
  "major challenge","salary expectations","your resume","your cv","about yourself","this role","your background",
]);

const SIGNALS = {
  elevator:["resume","cv","background","summary","about","yourself","experience","journey","story","work history"],
  role:["why","role","company","join","attracted","us"],
  strengths:["strength","strengths","good at","gud at","best at","superpower","strong at"],
  weaknesses:["weakness","weaknesses","improve","development","gap","area to improve"],
  roadblock:["challenge","roadblock","obstacle","failed","mistake","conflict","setback"],
  leadership:["lead","led","leadership","managed","mentor","ownership","team","influenced","high-performing","example"],
  career:["career","goals","five years","future","next step","direction","path"],
  pm:["project","stakeholder","timeline","delivery","plan","prioritize","align","organize","keep aligned"],
  metrics:["metric","metrics","kpi","success","impact","indicator","reporting","north star"],
  salary:["salary","compensation","pay","range","expectation","total compensation"],
} as const;

const INTENT_HEADS = new Set([
  "strength","strengths","weakness","weaknesses","metric","metrics","kpi","career","goal","goals",
  "project","stakeholder","salary","compensation","resume","cv","background","role","company",
  "challenge","challenges","lead","led","leadership","experience","program","programs","process","processes",
  "customer","okrs","roadmap","roadmaps","salesforce","teams","team","first","high-performing","relationships",
  "relationship","example","styles","style","partnership","communication","communications","adoption","outcomes"
]);

// ---------------- Title scrub/normalize ----------------

function compact(s: string): string { return (s ?? "").replace(/\s+/g, " ").trim(); }
function stripTrailingPunct(s: string): string { return s.replace(/[\s"')\]]*([.,;:!?]+)?\s*$/g, "").trim(); }
function normalizationKey(s: string): string { return stripTrailingPunct(compact(s)).toLowerCase(); }

function scrubSystemPrefixes(title: string): string {
  return (title || "")
    .replace(/^\s*(?:cue\s*board|cueboard|q\s*board)\s*\d*\s*[:\-–—]?\s*/i, "")
    .replace(/^\s*\d+\s*[:\-–—]\s+/, "")
    .trim();
}

function normalizeForMatch(raw: string): string {
  const t = scrubSystemPrefixes(raw);
  return normalizationKey(t.normalize("NFKC"));
}
function tokenizeLoose(s: string): string[] {
  return normalizeForMatch(s).split(/\s+/).filter(Boolean);
}

// ---------------- Canonical index + matching ----------------

type CanonNote = { title: string; triggers?: string[]; aliases?: string[] };
type Pack = { id: string; entries: CanonNote[] };

const CANONICAL_PACKS: Pack[] = [
  { id: "general",   entries: generalPack as any },
  { id: "ae",        entries: aePack as any },
  { id: "csm",       entries: csmPack as any },
  { id: "pm",        entries: pmPack as any },
  { id: "swe",       entries: swePack as any },
  { id: "marketing", entries: mktPack as any },
];

type CanonIdx = {
  pack: string; title: string; titleNorm: string; tokenSet: Set<string>;
  triggers: string[]; aliasNorms: string[];
};

const CANON_INDEX: CanonIdx[] = (() => {
  const out: CanonIdx[] = [];
  for (const pack of CANONICAL_PACKS) {
    for (const e of (pack.entries as any[])) {
      const title = (e?.title || "").trim();
      if (!title) continue;
      const tNorm = normalizeForMatch(title);
      const tokens = new Set(tokenizeLoose(title));
      const aliasNorms = Array.isArray(e?.aliases) ? (e.aliases as string[]).map(a => normalizeForMatch(a)) : [];
      const triggers = Array.isArray(e?.triggers) ? (e.triggers as string[]).map(x => (x || "").trim()).filter(Boolean) : [];
      out.push({ pack: pack.id, title, titleNorm: tNorm, tokenSet: tokens, triggers, aliasNorms });
    }
  }
  return out;
})();

function jaccard(a: Set<string>, b: Set<string>): number {
  let inter = 0; a.forEach(x => { if (b.has(x)) inter++; });
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

function levenshteinNorm(a: string, b: string): number {
  const s = a, t = b; const n = s.length, m = t.length;
  if (n === 0 && m === 0) return 1; if (n === 0 || m === 0) return 0;
  const dp = Array.from({length:n+1},()=>new Array(m+1).fill(0));
  for (let i=0;i<=n;i++) dp[i][0]=i; for (let j=0;j<=m;j++) dp[0][j]=j;
  for (let i=1;i<=n;i++) {
    const si=s.charCodeAt(i-1);
    for (let j=1;j<=m;j++) {
      const cost = si===t.charCodeAt(j-1)?0:1;
      dp[i][j]=Math.min(dp[i-1][j]+1, dp[i][j-1]+1, dp[i-1][j-1]+cost);
    }
  }
  const dist=dp[n][m]; return 1 - dist/Math.max(n,m);
}

function scoreTitleAgainst(entry: CanonIdx, noteTitle: string): number {
  const t = normalizeForMatch(noteTitle);
  const j = jaccard(new Set(tokenizeLoose(t)), entry.tokenSet);
  const l = levenshteinNorm(t, entry.titleNorm);
  let aliasBoost = 0; for (const al of entry.aliasNorms) aliasBoost = Math.max(aliasBoost, levenshteinNorm(t, al));
  return 0.6*j + 0.4*Math.max(l, aliasBoost);
}

function matchCanonical(noteTitle: string): { entry: CanonIdx; score: number } | null {
  if (!noteTitle?.trim()) return null;
  const scored = CANON_INDEX.map(e => ({ entry:e, score:scoreTitleAgainst(e, noteTitle) }))
                            .sort((a,b)=>b.score-a.score);
  const top = scored[0]; if (!top) return null;
  if (top.score < 0.85) return null;
  const second = scored[1];
  if (second && (top.score - second.score) < 0.02) {
    if (top.entry.pack !== "general" && second.entry.pack === "general") return second;
    if (top.entry.pack === second.entry.pack) return [top,second].sort((x,y)=>x.entry.title.localeCompare(y.entry.title))[0];
  }
  return top;
}

// ---------------- Text utils ----------------

function wordCount(s: string): number { return compact(s).split(/\s+/).filter(Boolean).length; }
function nonSpaceLen(s: string): number { return (s ?? "").replace(/\s+/g, "").length; }

function isAcronym(s: string): boolean { return /^[A-Z]{2,5}$/.test(compact(s)); }
function isProperName(s: string): boolean { return /^[A-Z][a-z]+$/.test(compact(s)); }
function isAcronymOrProper(s: string): boolean { return isAcronym(s) || isProperName(s); }

// Stopwords + clitics
const STOPWORDS = new Set([
  "the","a","an","to","of","and","or","for","in","on","with","about","at","by","from","is","are","am","be","been","being","that","this","it","as","we","you","your","our","their","my","i","me","us","they","he","she","them","was","were","do","does","did",
  "ve","re","m","ll","d","t","s"
]);

const START_PREP_OR_CONJ = new Set(["across","for","and","but","so","yet","nor","or","to","at","by","in","on","with","about","from","into","over","after","before","between","without","within"]);

// ---------------- Hygiene helpers ----------------

// Final anti-mash (used in non-canonical finalize only)
function containsHeadMashup(k: string): boolean {
  const cs = /\bcommunication\s+styles\b/;
  if (cs.test(k)) {
    if (/\b(goals|challenges|partnership|outcomes|adoption)\b(?!\s*(and|or|,))[\w-]*\s+\bcommunication\s+styles\b/.test(k)) return true;
    if (/\bcommunication\s+styles\b(?!\s*(and|or|,))[\w-]*\s+\b(goals|challenges|partnership|outcomes|adoption)\b/.test(k)) return true;
  }
  if (/\bgoals\b(?!\s*(and|or|,))[\w-]*\s+\bchallenges\b/.test(k)) return true;
  if (/\bchallenges\b(?!\s*(and|or|,))[\w-]*\s+\bgoals\b/.test(k)) return true;
  return false;
}

function looksLikeTimingNoise(t: string): boolean {
  if (/^\d/.test(t)) return true;
  if (/\b\d+\s*(seconds?|minutes?|mins?|hrs?|hours?)\b/i.test(t)) return true;
  if (/\bmax\b/i.test(t) && /\d/.test(t)) return true;
  return false;
}

const BAD_END_TOKENS = new Set(["where","whether","so","then","there"]);
const BAD_TOKENS_ANYWHERE = new Set(["where","whether"]);

// Safer singularization
function singularizeLite(tok: string): string {
  const t = tok.toLowerCase();
  if (t.endsWith("ly")) return t;
  if (t === "teams") return "team";
  if (t === "stakeholders") return "stakeholder";
  if (t === "processes") return "process";
  if (t === "programs") return "program";
  if (t === "roadmaps") return "roadmap";
  if (t === "kpis") return "kpi";
  if (t === "metrics") return "metrics";
  return t;
}

const FLUFFY_VERB_START = new Set(["enjoy","love","like"]);
const REJECT_GERUND_START = new Set(["understanding"]);
const REJECT_FEEL_WORD = /(?:\bfeel(?:s|ing)?\b)/i;

function isAwkCustomerTail(p: string): boolean {
  const k = normalizationKey(p);
  if (!/\bcustomers?\b$/.test(k)) return false;
  if (/\bcustomer first\b/.test(k)) return false;
  if (/\bcustomer success\b/.test(k)) return false;
  if (/\bunderstand customer\b/.test(k)) return false;
  if (/\bcustomer-?centric\b/.test(k)) return false;
  return true;
}

function endsNouny(tokens: string[]): boolean {
  if (!tokens.length) return false;
  const last = tokens[tokens.length - 1];
  if (/ing$/.test(last)) return false;
  const nouny = new Set([
    "resume","cv","company","role","team","stakeholder","stakeholders","process","processes","metrics","goals","plan","plans","management","success","pressure","hygiene","issues","history","direction","path","impact","programs","program","okrs","roadmap","roadmaps","salesforce","customer","first","teams","leadership","relationships","relationship","example","styles","partnership","challenges","adoption","outcomes"
  ]);
  return nouny.has(last);
}

function isHighQualityTwoToken(p: string): boolean {
  const toks = tokenize(p);
  if (toks.length !== 2) return false;
  const [a,b] = toks;
  if (/-/.test(a) && b) return true;                  // high-performing teams
  if (a === "customer" && b === "first") return true; // customer first
  return false;
}

function looksHeaded(p: string): boolean {
  const toks = tokenize(p).map(singularizeLite);
  if (isHighQualityTwoToken(p)) return true;
  for (const t of toks) if (INTENT_HEADS.has(t)) return true;
  return false;
}

function sanitizeCandidate(s: string, opts?: { allowOneWord?: boolean }): string | null {
  let t = stripTrailingPunct(compact(s));
  if (!t) return null;
  if (looksLikeTimingNoise(t)) return null;
  if (t.length > 60) return null;

  const firstTok = t.split(/\s+/)[0]?.toLowerCase();
  if (firstTok && START_PREP_OR_CONJ.has(firstTok)) return null;
  if (firstTok && FLUFFY_VERB_START.has(firstTok)) return null;
  if (firstTok && REJECT_GERUND_START.has(firstTok)) return null;
  if (REJECT_FEEL_WORD.test(t)) return null;
  if (/\b[ai]\b$/i.test(t)) return null;
  if (/\btons?\s+of\s+/i.test(t)) return null;

  const wc = wordCount(t);
  if (wc === 1) {
    const low = t.toLowerCase();
    if (RISKY_SINGLETON_BLOCKLIST.has(low)) return null;
    if (isAcronymOrProper(t)) return t;
    if (!opts?.allowOneWord) return null;
  }
  if (wc > 7) return null;

  const k = normalizationKey(t);
  if (containsHeadMashup(k)) return null;
  if (isAwkCustomerTail(k)) return null;

  const toks = tokenize(k);
  if (toks.length && BAD_END_TOKENS.has(toks[toks.length - 1])) return null;
  for (const tok of toks) if (BAD_TOKENS_ANYWHERE.has(tok)) return null;

  if (!looksHeaded(t) && !isHighQualityTwoToken(t)) return null;
  if (!endsNouny(toks) && !isHighQualityTwoToken(t)) return null;

  return t;
}

function dedupeCasePreserve(arr: string[]): string[] {
  const seen = new Set<string>(); const out: string[] = [];
  for (let i = 0; i < arr.length; i++) {
    const raw = arr[i];
    const s = stripTrailingPunct(compact(raw));
    if (!s) continue;
    const k = normalizationKey(s);
    if (seen.has(k)) continue;
    seen.add(k); out.push(s);
  }
  return out;
}

// ---------------- Parsing helpers ----------------

function stripMd(s: string): string { return s.replace(/[*_`]+/g, ""); }
function extractBullets(md: string): string[] {
  return (md ?? "")
    .split(/\r?\n/)
    .map(line => { const m = line.match(/^\s*[-*]\s+(.*)$/); return m ? stripMd(m[1]) : ""; })
    .filter(Boolean)
    .flatMap(base => base.split(";").map(f => compact(f)).filter(Boolean));
}

function extractBodyProse(md: string): string {
  const lines = (md ?? "").split(/\r?\n/);
  const prose: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = stripMd(lines[i] || "");
    if (!line) continue;
    if (/^\s*[-*]\s+/.test(lines[i])) continue;
    prose.push(line.trim());
  }
  return compact(prose.join(" "));
}

function splitIntoSentences(s: string): string[] {
  return s.split(/[.!?;()\u2013\u2014]/g).map(compact).filter(Boolean);
}

// --------- Token-level punctuation scrub + contractions ---------
function cleanToken(tok: string): string {
  return tok.replace(/^[^a-z0-9]+|[^a-z0-9-]+$/gi, "");
}

function tokenize(s: string): string[] {
  const pre = compact(s).replace(/[\u2019\u2018']/g, " ");
  return pre
    .toLowerCase()
    .split(/\s+/)
    .map(cleanToken)
    .filter(tok => tok && !STOPWORDS.has(tok));
}

function ngrams(tokens: string[], n: number): string[] {
  const out: string[] = [];
  for (let i=0;i+n<=tokens.length;i++) out.push(tokens.slice(i,i+n).join(" "));
  return out;
}

// ---------------- Content signals → concept inference ----------------

type Concept = keyof typeof SIGNALS;

function inferConcepts(title: string, bullets: string[]): { strong: Concept[]; weak: Concept[] } {
  const hay = [title, ...bullets].join(" ").toLowerCase();
  const scores: Record<Concept, number> = {
    elevator:0, role:0, strengths:0, weaknesses:0, roadblock:0, leadership:0, career:0, pm:0, metrics:0, salary:0
  };
  for (const concept in SIGNALS) {
    const sigs = (SIGNALS as any)[concept] as string[];
    for (let i=0;i<sigs.length;i++) {
      const sig = sigs[i];
      const re = new RegExp(`\\b${sig.replace(/\s+/g,"\\s+")}\\b`,"i");
      if (re.test(hay)) (scores as any)[concept] += 1;
    }
  }
  const ranked = (Object.keys(scores) as Concept[]).sort((a,b)=>scores[b]-scores[a]);
  const strong = ranked.filter(c=>scores[c]>=2).slice(0,2);
  const weak = strong.length ? [] : ranked.filter(c=>scores[c]===1).slice(0,1);
  return { strong, weak };
}

// ---------------- Preserve before mining ----------------

const PRESERVE_PHRASES = [
  "lead by example"
];

function extractPreservePhrases(text: string): string[] {
  const hay = normalizationKey(text);
  const out: string[] = [];
  for (let i=0;i<PRESERVE_PHRASES.length;i++) {
    const p = PRESERVE_PHRASES[i];
    const re = new RegExp(`\\b${p.replace(/\s+/g,"\\s+")}\\b`, "i");
    if (re.test(hay)) out.push(p);
  }
  return out;
}

// ---------------- Raw-text harvesters ----------------

function harvestProblemSolving(text: string): string[] {
  const out: string[] = [];
  const hay = normalizationKey(text);
  const re = /\b(solve|solving)\b[\w\s-]{0,50}\b(for|with)\b[\w\s-]{0,8}\bcustomers?\b/gi;
  if (re.test(hay)) out.push("solve customer problems");
  return out;
}

// Pull clean CSM heads from sentences mentioning customers
function harvestCustomerHeads(text: string): string[] {
  const out: string[] = [];
  const sents = splitIntoSentences(text);
  for (const sent of sents) {
    const s = sent.toLowerCase();
    if (!/\bcustomers?\b/.test(s) && !/\bcustomer\b/.test(s)) continue;
    if (/\bgoals?\b/.test(s)) out.push("customer goals");
    if (/\bchallenges?\b/.test(s)) out.push("customer challenges");
    if (/\bcommunication\s+styles?\b/.test(s)) out.push("communication styles");
    if (/\bpartnership\b/.test(s)) out.push("customer partnership");
    if (/\btailor(?:ed)?\b/.test(s) && /\b(guidance|approach)\b/.test(s)) out.push("tailored approach");
  }
  return dedupeCasePreserve(out);
}

// leadership phrase detection
function sourceHasLeadByExample(text: string): boolean { return /\blead\s+by\s+example\b/i.test(text); }
function sourceHasLedHighPerfTeams(text: string): boolean {
  return /\bled\b[^.]{0,40}\bhigh-?performing\b[^.]{0,12}\bteams?\b/i.test(text) ||
         /\bhigh-?performing\b[^.]{0,12}\bteams?\b/i.test(text);
}

// ---------------- Candidate mining ----------------

function minePhraseCandidates(title: string, bullets: string[], prose: string): string[] {
  const bodies = [title, ...(bullets[0] ? [bullets[0]] : []), ...bullets.slice(1), prose].filter(Boolean);
  const rawText = [title, ...bullets, prose].filter(Boolean).join(" ");
  const cands: string[] = [];
  const seen = new Set<string>();

  // Preserve + harvesters first (pool only; seeds protected separately)
  const pres = [
    ...extractPreservePhrases(rawText),
    ...harvestProblemSolving(rawText),
    ...harvestCustomerHeads(rawText),
  ];
  pres.forEach(p => { const k = normalizationKey(p); if (!seen.has(k)) { seen.add(k); cands.push(p); } });

  // Standard n-gram mining (3,2,4) with strict sanitize
  for (let b=0;b<bodies.length;b++) {
    const sents = splitIntoSentences(bodies[b]);
    for (let si=0; si<sents.length; si++) {
      const toks = tokenize(sents[si]).map(singularizeLite);
      for (let idx=0; idx<toks.length; idx++) {
        for (const N of [3,2,4]) {
          if (idx + N > toks.length) continue;
          const span = toks.slice(idx, idx+N).join(" ");
          const s = sanitizeCandidate(span);
          if (!s) continue;
          const k = normalizationKey(s);
          if (seen.has(k)) continue;
          seen.add(k);
          cands.push(s);
        }
      }
    }
  }
  return cands;
}

// ---------------- Phase-2 helpers ----------------

const NEAR_DUP_JACCARD = 0.7;

function phraseTokenSet(s: string): Set<string> {
  return new Set(tokenize(s).map(singularizeLite));
}
function isNearDuplicate(candidate: string, kept: string[]): boolean {
  const candSet = phraseTokenSet(candidate);
  for (let i = 0; i < kept.length; i++) {
    const kSet = phraseTokenSet(kept[i]);
    if (jaccard(candSet, kSet) >= NEAR_DUP_JACCARD) return true;
  }
  return false;
}

function isSubsetDuplicate(candidate: string, kept: string[]): boolean {
  const cand = phraseTokenSet(candidate);
  for (let i = 0; i < kept.length; i++) {
    const base = phraseTokenSet(kept[i]);
    const candInBase = Array.from(cand).every(t => base.has(t));
    const baseInCand = Array.from(base).every(t => cand.has(t));
    if (candInBase || baseInCand) return true;
  }
  return false;
}

function filterNearDuplicates(list: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < list.length; i++) {
    const p = list[i];
    if (isNearDuplicate(p, out)) continue;
    out.push(p);
  }
  return out;
}

function isAdjectiveTailFragment(p: string): boolean {
  const toks = tokenize(p);
  if (!toks.length) return false;
  const last = toks[toks.length - 1];
  if (/^[a-z]+-[a-z]+$/.test(last)) return true;
  if (/ing$/.test(last)) return true;
  return false;
}

// Customer-first canonicalization
function isPutCustomerFirstFamily(k: string): boolean { return /\bput\b/.test(k) && /\bcustomer\b/.test(k) && /\bfirst\b/.test(k); }
function isCustomerPartialReject(k: string): boolean { return /\bcustomer\b/.test(k) && /\bput\b/.test(k) && !/\bfirst\b/.test(k); }
function canonicalizeCustomerFirst(p: string): string | null {
  const k = normalizationKey(p);
  if (isCustomerPartialReject(k)) return null;
  if (isPutCustomerFirstFamily(k)) return "put the customer first";
  return p;
}

function phraseHeadLemma(p: string): string {
  const toks = tokenize(p).map(singularizeLite);
  return toks.length ? toks[toks.length - 1] : "";
}

// Special pairs (allow keeping both)
function isLedTeamsPair(a: string, b: string): boolean {
  const ka = normalizationKey(a), kb = normalizationKey(b);
  const hasLedA = /\bled\b/.test(ka) && /\bteam(s)?\b/.test(ka);
  const hasLedB = /\bled\b/.test(kb) && /\bteam(s)?\b/.test(kb);
  const hasNP_A = !/\bled\b/.test(ka) && /\bhigh-?performing\b/.test(ka) && /\bteam(s)?\b/.test(ka);
  const hasNP_B = !/\bled\b/.test(kb) && /\bhigh-?performing\b/.test(kb) && /\bteam(s)?\b/.test(kb);
  return (hasLedA && hasNP_B) || (hasLedB && hasNP_A);
}
function isCustomerFirstPair(a: string, b: string): boolean {
  const ka = normalizationKey(a), kb = normalizationKey(b);
  const isCF = (k: string) => /\bcustomer\b/.test(k) && /\bfirst\b/.test(k);
  if (!(isCF(ka) && isCF(kb))) return false;
  const hasPut = /\bput\b/.test(ka) || /\bput\b/.test(kb);
  const hasBare = !/\bput\b/.test(ka) || !/\bput\b/.test(kb);
  return hasPut && hasBare;
}
function isSpecialPair(a: string, b: string): boolean { return isLedTeamsPair(a, b) || isCustomerFirstPair(a, b); }

function deriveCoreNounFromTitle(title: string): string | null {
  const norm = scrubSystemPrefixes(title);
  const toks = tokenize(norm).map(singularizeLite).filter(t => !STOPWORDS.has(t));
  if (!toks.length) return null;
  const head = toks[toks.length - 1];
  if (!head || GENERIC_CORE_NOUN_BLOCKLIST.has(head)) return null;
  if (RISKY_SINGLETON_BLOCKLIST.has(head)) return null;
  return head;
}

function pluralizeCoreNounIfNatural(core: string): string {
  if (core === "relationship") return "relationships";
  return core;
}

// ---------------- Scoring ----------------

type Scored = { p: string; score: number };

function scorePool(pool: string[]): Scored[] {
  return pool.map(p => {
    const toks = tokenize(p);
    const nouny = endsNouny(toks) ? 1 : 0;
    const len = toks.length;
    const lenBonus = (len === 2 ? 3 : (len === 3 ? 4 : (len === 4 ? 3 : 0)));
    let hasHead = 0; for (let t=0;t<toks.length;t++) if (INTENT_HEADS.has(toks[t])) { hasHead = 4; break; }
    const allowTwo = (TWO_TOKEN_ALLOWLIST.has(normalizationKey(p)) || isHighQualityTwoToken(p)) ? 3 : 0;
    const startPenalty = START_PREP_OR_CONJ.has((toks[0]||"")) ? -3 : 0;
    const csmBoost = /\bcustomer (goals|challenges|partnership)\b/.test(p) || /\bcommunication styles\b/.test(p) ? 3 : 0;
    const score = hasHead + lenBonus + nouny + allowTwo + startPenalty + csmBoost;
    return { p, score };
  }).sort((a,b)=>b.score-a.score);
}

// ---------------- Assembly (length-aware + clustering) ----------------

type AssemblyBudget = { targetMax: number; hardMax: number; perClusterCap: number };

function classifyBudgetByLength(sents: number): AssemblyBudget {
  if (sents <= 2) return { targetMax: 7, hardMax: 8, perClusterCap: 2 };
  if (sents <= 6) return { targetMax: 8, hardMax: 10, perClusterCap: 2 };
  return { targetMax: 10, hardMax: 10, perClusterCap: 2 };
}

function assembleWithCapsAndClusters(
  scored: Scored[],
  outPrim: string[],
  outAssist: string[],
  pinnedSet: Set<string>,
  budget: AssemblyBudget,
  title: string,
  rawText: string
): { prim: string[]; assist: string[] } {
  const keptPrim: string[] = [...outPrim];
  const keptAssist: string[] = [...outAssist];

  const seen = new Set([...keptPrim, ...keptAssist].map(normalizationKey));
  const clusterCounts: Record<string, number> = {};
  const bumpCluster = (h: string) => { clusterCounts[h] = (clusterCounts[h] ?? 0) + 1; };
  const underClusterCap = (h: string) => (clusterCounts[h] ?? 0) < budget.perClusterCap;

  const titleNorm = normalizationKey(title);
  const titleIsLeadership = /\bleadership\b/.test(titleNorm);
  const titleIsRelationships = /\brelationship(s)?\b/.test(titleNorm);

  for (let i=0; i<scored.length && keptPrim.length < budget.targetMax; i++) {
    let p = scored[i].p;

    const canon = canonicalizeCustomerFirst(p);
    if (canon === null) continue;
    p = canon;

    const k = normalizationKey(p);
    if (seen.has(k) || pinnedSet.has(k)) continue;
    if (isAdjectiveTailFragment(p)) continue;

    const wc = wordCount(p);
    const ns = nonSpaceLen(p);
    if (wc === 2 && !isHighQualityTwoToken(p) && !TWO_TOKEN_ALLOWLIST.has(k)) continue;
    if (wc > 4 || ns < 8) continue;

    const wouldBeSubsetDup = isSubsetDuplicate(p, keptPrim);
    let allowWithinCluster = false;
    if (wouldBeSubsetDup) {
      allowWithinCluster = keptPrim.some(existing => isSpecialPair(existing, p));
      if (!allowWithinCluster) continue;
    } else if (isNearDuplicate(p, keptPrim)) {
      const ok = keptPrim.some(existing => isSpecialPair(existing, p));
      if (!ok) continue;
    }

    const head = phraseHeadLemma(p);
    if (!underClusterCap(head)) {
      const okPair = keptPrim.some(existing => isSpecialPair(existing, p));
      if (!okPair) continue;
    }

    keptPrim.push(p);
    seen.add(k);
    bumpCluster(head);
  }

  const wantAssist = Math.max(0, Math.min(3, budget.hardMax - keptPrim.length));
  if (wantAssist > 0) {
    const primHasCustomer = keptPrim.some(t => /\bcustomer\b/.test(t.toLowerCase()));
    const maybeAssist: string[] = [];

    if (primHasCustomer) maybeAssist.push("customer-centric");
    if (titleIsRelationships) maybeAssist.push("relationship-building");
    if (titleIsLeadership) {
      maybeAssist.push("led a team","took ownership","influenced without authority","lead by example");
    }

    const scoredAssist = scorePool(dedupeCasePreserve(maybeAssist));
    for (let i=0; i<scoredAssist.length && keptAssist.length < wantAssist; i++) {
      const p = scoredAssist[i].p;
      const k = normalizationKey(p);
      if (seen.has(k) || pinnedSet.has(k)) continue;
      if (isNearDuplicate(p, [...keptPrim, ...keptAssist])) continue;
      keptAssist.push(p);
      seen.add(k);
    }
  }

  const total = keptPrim.length + keptAssist.length;
  if (total > budget.hardMax) {
    const overflow = total - budget.hardMax;
    keptAssist.splice(Math.max(0, keptAssist.length - overflow), overflow);
  }

  return { prim: keptPrim, assist: keptAssist };
}

// ---------------- Finalization ----------------

function finalizeTriggers(
  prim: string[],
  assist: string[],
  title: string,
  bullets: string[],
  hardMax: number,
  opts?: { allowTitleAnchor?: boolean; dropMashes?: boolean; applyNearDup?: boolean }
): string[] {
  // exact de-dup only (case-insensitive)
  let pList = dedupeCasePreserve(prim);
  let aList = dedupeCasePreserve(assist);

  const applyNearDup = opts?.applyNearDup !== false;

  if (applyNearDup) {
    pList = filterNearDuplicates(pList);
    aList = filterNearDuplicates(aList).filter(a => !isNearDuplicate(a, pList));
  } else {
    const setP = new Set(pList.map(normalizationKey));
    aList = aList.filter(a => !setP.has(normalizationKey(a)));
  }

  // Optional: drop glued heads (non-canonical path)
  const dropMashes = opts?.dropMashes !== false;
  if (dropMashes) {
    pList = pList.filter(p => !containsHeadMashup(normalizationKey(p)));
    aList = aList.filter(a => !containsHeadMashup(normalizationKey(a)));
  }

  // Optional: add title anchor (disabled for canonical path)
  const allowAnchor = opts?.allowTitleAnchor !== false;
  if (allowAnchor) {
    const core0 = deriveCoreNounFromTitle(title);
    const core = core0 ? pluralizeCoreNounIfNatural(core0) : null;
    if (core) {
      const k = normalizationKey(core);
      const hasCore = pList.some(t => normalizationKey(t) === k) || aList.some(t => normalizationKey(t) === k);
      if (!hasCore && pList.length + aList.length < hardMax) aList.push(core);
    }
  }

  return [...pList, ...aList].slice(0, hardMax);
}

// Seed-safe Leadership salvage: add phrases only if there is capacity
function salvageLeadershipIfRoom(
  result: string[],
  hardMax: number,
  title: string,
  rawText: string
): string[] {
  const titleIsLeadership = /\bleadership\b/.test(normalizationKey(title));
  if (!titleIsLeadership) return result;

  const need: string[] = [];
  if (sourceHasLeadByExample(rawText)) need.push("lead by example");
  if (sourceHasLedHighPerfTeams(rawText)) need.push("led high-performing teams");

  if (!need.length) return result;

  const seen = new Set(result.map(normalizationKey));
  const out = [...result];
  for (const p of need) {
    const k = normalizationKey(p);
    if (seen.has(k)) continue;
    if (out.length >= hardMax) break; // never displace seeds
    out.push(p);
    seen.add(k);
  }
  return out;
}

// Ensure clean CSM heads survive in non-canonical notes
function enforceCSMHeadsIfPresent(rawText: string, title: string, prim: string[], assist: string[], hardMax: number): { prim: string[]; assist: string[] } {
  const hay = (rawText || "") + " " + (title || "");
  const looksCSM = /\bcustomer\b/i.test(hay);
  if (!looksCSM) return { prim, assist };

  const cleanHeads = harvestCustomerHeads(rawText);
  if (!cleanHeads.length) return { prim, assist };

  const seen = new Set([...prim, ...assist].map(normalizationKey));
  const outPrim = [...prim];
  const outAssist = [...assist];

  for (const h of cleanHeads) {
    const k = normalizationKey(h);
    if (seen.has(k)) continue;
    if (outPrim.length + outAssist.length >= hardMax) break;
    outPrim.push(h);
    seen.add(k);
  }
  return { prim: outPrim, assist: outAssist };
}

// ---------------- Build per-note ----------------

export function buildTriggersForNote(note: CueNote, _opts?: BuilderOptions): string[] {
  const rawTitle = (note.title ?? "").trim();
  const title = scrubSystemPrefixes(rawTitle);
  const bullets = extractBullets(note.body_markdown ?? "");
  const prose = extractBodyProse(note.body_markdown ?? "");
  const rawText = [title, ...bullets, prose].filter(Boolean).join(" ");
  const sents = splitIntoSentences(rawText).length || 1;
  const budget = classifyBudgetByLength(sents);

  const pinnedRaw = Array.isArray(note.triggers) ? note.triggers : [];
  const pinned = dedupeCasePreserve(pinnedRaw);
  const pinnedSet = new Set(pinned.map(normalizationKey));

  const match = matchCanonical(title);
  const log = builderLogOn();
  if (log) console.debug("[Builder v72.19] title=", rawTitle, "→", title, "match=", match?.entry?.pack, match?.entry?.title, "score=", match?.score?.toFixed?.(3), "budget=", budget, "sents=", sents);

  // Start pools
  const prim: string[] = [];
  const assist: string[] = [];

  // 1) pinned (safe)
  for (let i=0; i<pinned.length && prim.length < DEFAULT_MAX.total; i++) {
    const canon = canonicalizeCustomerFirst(pinned[i]);
    if (canon && !isAdjectiveTailFragment(canon)) prim.push(canon);
  }

  // ---- Canonical path (protect seeds 10/10) ----
  if (match && match.entry.triggers && match.entry.triggers.length) {
    // 2) seeds (exact-dedupe only)
    const seeds = dedupeCasePreserve(match.entry.triggers)
      .map(canonicalizeCustomerFirst)
      .filter((x): x is string => !!x);

    const seen = new Set(prim.map(normalizationKey));
    for (let i=0; i<seeds.length && prim.length < DEFAULT_MAX.total; i++) {
      const k = normalizationKey(seeds[i]);
      if (seen.has(k)) continue;
      prim.push(seeds[i]);
      seen.add(k);
    }

    // finalize WITHOUT title-anchor, WITHOUT anti-mash, WITHOUT near-dup pruning (seed-locked)
    let result = finalizeTriggers(prim, [], title, bullets, DEFAULT_MAX.total, {
      allowTitleAnchor: false,
      dropMashes: false,
      applyNearDup: false
    });

    // seed-safe Leadership salvage (only if room)
    result = salvageLeadershipIfRoom(result, DEFAULT_MAX.total, title, rawText);
    return result.slice(0, DEFAULT_MAX.total);
  }

  // ---- Non-canonical path ----

  // 2) mine candidates (includes preserved/harvested items in pool)
  const candidates = dedupeCasePreserve(minePhraseCandidates(title, bullets, prose))
    .map(canonicalizeCustomerFirst)
    .filter((x): x is string => !!x);

  // 3) concept enrichment
  const inferred = inferConcepts(title, bullets);
  const enrich: string[] = [];
  for (let i=0;i<inferred.strong.length;i++) {
    const concept = inferred.strong[i];
    const tpls = (VETTED_TEMPLATES as any)[concept] as string[];
    for (let j=0;j<tpls.length;j++) enrich.push(tpls[j]);
    if (concept === "strengths") enrich.push("strength");
    if (concept === "weaknesses") enrich.push("weakness");
    if (concept === "metrics")   enrich.push("metrics");
    if (concept === "career")    enrich.push("career");
  }

  // 4) assemble
  const pool = dedupeCasePreserve([...candidates, ...enrich]);
  const staged = assembleWithCapsAndClusters(scorePool(pool), prim, assist, pinnedSet, budget, title, rawText);

  // 5) CSM heads enforcement (budget-respecting)
  const enforced = enforceCSMHeadsIfPresent(rawText, title, staged.prim, staged.assist, budget.hardMax);

  // 6) finalize (with title anchor & anti-mash, with near-dup)
  let result = finalizeTriggers(enforced.prim, enforced.assist, title, bullets, budget.hardMax, {
    allowTitleAnchor: true,
    dropMashes: true,
    applyNearDup: true
  });

  // 7) Leadership salvage (only if room)
  result = salvageLeadershipIfRoom(result, budget.hardMax, title, rawText);

  // 8) template top-up if still under target
  if (result.length < budget.targetMax) {
    const maybe: string[] = [];
    const concept = inferred.strong[0];
    if (concept && VETTED_TEMPLATES[concept]) maybe.push(...VETTED_TEMPLATES[concept]);
    if (maybe.length) {
      const topped = dedupeCasePreserve([...result, ...maybe]);
      const staged2 = assembleWithCapsAndClusters(scorePool(topped), [], [], pinnedSet, budget, title, rawText);
      const enforced2 = enforceCSMHeadsIfPresent(rawText, title, staged2.prim, staged2.assist, budget.hardMax);
      result = finalizeTriggers(enforced2.prim, enforced2.assist, title, bullets, budget.hardMax, {
        allowTitleAnchor: true,
        dropMashes: true,
        applyNearDup: true
      });
      result = salvageLeadershipIfRoom(result, budget.hardMax, title, rawText);
    }
  }

  return result.slice(0, budget.hardMax);
}

// ---------------- Packet helper ----------------

export function buildTriggersForNotes(notes: CueNote[], _opts?: BuilderOptions): CueNote[] {
  // Per-note only; no cross-note suppression so Starter Packs retain 10/10 per board
  return notes.map(n => ({ ...n, triggers: buildTriggersForNote(n, _opts) }));
}

// ---------------- Logging flag ----------------
function builderLogOn(): boolean { try { return (localStorage.getItem("cb_builder_log") || "").toLowerCase() === "true"; } catch { return false; } }
